import { FormikHelpers, Formik, Form } from "formik";
import _ from "lodash";
import { useSnackbar } from "notistack";
import * as yup from "yup";

import { Card, CardHeader, CardContent } from "@material-ui/core";

import {
  CONFIRMATION_TEXT,
  ApprovalFields
} from "src/components/ApprovalFields";
import { useTenant } from "src/components/LoggedInAdminProvider";
import FormikErrorBanner from "src/components/form/FormikErrorBanner";
import SubmitButton from "src/components/form/SubmitButton";
import { ApprovalRequirement } from "src/graphql/generated/globalTypes";
import {
  tenantEditApprovalRequired,
  tenantEditApprovalRequiredVariables
} from "src/graphql/generated/tenantEditApprovalRequired";
import { EDIT_TENANT_APPROVAL_REQUIRED } from "src/graphql/tenant";
import useMutation from "src/util/useMutation";

type Values = {
  approval_requirement: ApprovalRequirement;
  approverRole: string;
  voicemail_allowed: boolean;
};

export default function Basics(): JSX.Element {
  const tenant = useTenant()!;
  const snackbar = useSnackbar();

  const schema = yup.object().shape({
    approval_requirement: yup.string().required(),
    approverRole: yup.string(),
    voicemail_allowed: yup.boolean().required()
  });

  const [updateTenantApprovalRequired] = useMutation<
    tenantEditApprovalRequired,
    tenantEditApprovalRequiredVariables
  >(EDIT_TENANT_APPROVAL_REQUIRED, {
    onErrorSnackbarMessage: `Failed to update organization settings.`
  });

  async function handleSubmit(values: Values, helpers: FormikHelpers<Values>) {
    const { errors, data } = await updateTenantApprovalRequired({
      variables: {
        ...values,
        confirmationTextAtTimeOfUpdate: CONFIRMATION_TEXT
      }
    });

    if (!errors) {
      snackbar.enqueueSnackbar("Organization updated.", { variant: "info" });
      helpers.resetForm({
        values: {
          approval_requirement:
            data!.tenantEditApprovalRequired.approval_requirement,
          voicemail_allowed: data!.tenantEditApprovalRequired.voicemail_allowed,
          approverRole: ""
        }
      });
    }
  }

  return (
    <>
      <Card>
        <CardHeader title="Pre-approval and Voicemail Compliance" />
        <CardContent>
          <Formik
            initialValues={{
              approval_requirement: tenant.approval_requirement,
              approverRole: "",
              voicemail_allowed: tenant.voicemail_allowed
            }}
            onSubmit={handleSubmit}
            validationSchema={schema}
          >
            {({ isSubmitting, dirty }) => (
              <Form>
                <FormikErrorBanner />
                <ApprovalFields />
                <SubmitButton
                  type="submit"
                  disabled={!dirty}
                  isSubmitting={isSubmitting}
                  submitText="Update"
                  loadingText="Updating..."
                />
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </>
  );
}
